import type { GameAction, InputFrame } from "@golazo/shared";
import { normalize, vec } from "../core/math.js";
import type { InputSource } from "./keyboard.js";

/**
 * Un mando (Gamepad API, mapeo estandar: Xbox, PlayStation, Switch Pro…).
 *
 * `slot` es el orden entre los mandos CONECTADOS, no el indice del navegador:
 * si se desconecta el primero, el segundo pasa a ser el del jugador 1 en vez de
 * dejarlo sin mando. Se lee en cada tick —la API no tiene eventos de botones—,
 * y sin mando conectado devuelve un fotograma vacio: combinado con el teclado,
 * no estorba.
 *
 * Stick izquierdo o cruceta para moverse, A (Cruz) o gatillo derecho para
 * golpear, X (Cuadrado), B (Circulo) o boton superior derecho para el
 * regate/cuerpo. Los navegadores solo enseñan un mando despues de pulsar algun
 * boton con la pagina delante: hasta entonces, no existe.
 */
const DEADZONE = 0.35;
/** Un eje "en reposo" (ver `readyFor`): cerca del centro. */
const REST = 0.2;
const KICK_BUTTONS = [0, 7];
const DASH_BUTTONS = [2, 1, 5];
const DPAD = { up: 12, down: 13, left: 14, right: 15 } as const;

type Pads = ReadonlyArray<Gamepad | null>;

/**
 * Los mandos DE VERDAD primero.
 *
 * El navegador llama "mando" a cualquier aparato de juego USB: en Windows,
 * Firefox enseña palancas de vuelo, volantes, pedales… con `mapping` vacio.
 * Si uno de esos iba primero en la lista, se quedaba el sitio del jugador 1 y
 * el mando de verdad no hacia nada. Los de mapeo estandar (Xbox, PlayStation,
 * Switch Pro) van delante; el resto, por si alguien juega con uno raro.
 */
function connectedPad(slot: number, read: () => Pads): Gamepad | null {
  const connected = read().filter((pad): pad is Gamepad => pad !== null && pad.connected);
  const ordered = [
    ...connected.filter((pad) => pad.mapping === "standard"),
    ...connected.filter((pad) => pad.mapping !== "standard"),
  ];
  return ordered[slot] ?? null;
}

function down(pad: Gamepad, buttons: readonly number[], gate: { buttons: boolean[] } | null = null): boolean {
  return buttons.some((b) => pad.buttons[b]?.pressed === true && (!gate || gate.buttons[b] === true));
}

export function createGamepadInput(
  slot: number,
  read: () => Pads = () => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []),
): InputSource {
  const held = new Set<GameAction>();
  const pressed = new Set<GameAction>();
  const move = vec();
  const frame: InputFrame = { move, held, pressed };

  /*
   * Aparatos SIN mapeo estandar: un eje o un boton solo cuenta despues de
   * haberlo visto en reposo (el eje centrado, el boton suelto).
   *
   * Reportado el 2026-09-29 (teclado, Windows, Firefox): "a veces mi jugador
   * corria siempre hacia arriba". Un pedal o una palanca de gases que el
   * navegador enseña como mando tiene el eje 1 clavado en -1 en reposo, que
   * aqui es el stick hacia arriba; y como el teclado solo manda mientras pulsas
   * algo, al soltar las teclas el jugador salia corriendo hacia arriba solo. Un
   * stick de verdad esta centrado cuando no lo tocas, asi que con esto no cambia
   * nada; lo clavado no llega a contar nunca.
   */
  const ready = new Map<string, { axes: boolean[]; buttons: boolean[] }>();
  const readyFor = (pad: Gamepad): { axes: boolean[]; buttons: boolean[] } | null => {
    if (pad.mapping === "standard") return null;
    const key = `${pad.index}:${pad.id}`;
    let state = ready.get(key);
    if (!state) {
      state = { axes: [], buttons: [] };
      ready.set(key, state);
    }
    pad.axes.forEach((v, i) => {
      if (Math.abs(v) < REST) state.axes[i] = true;
    });
    pad.buttons.forEach((b, i) => {
      if (!b.pressed) state.buttons[i] = true;
    });
    return state;
  };

  const set = (action: GameAction, on: boolean): void => {
    if (on) {
      if (!held.has(action)) pressed.add(action);
      held.add(action);
    } else {
      held.delete(action);
    }
  };

  return {
    consume(): InputFrame {
      const pad = connectedPad(slot, read);
      if (!pad) {
        held.clear();
        move.x = 0;
        move.y = 0;
        return frame;
      }
      const gate = readyFor(pad);
      const axis = (i: number): number => (gate && !gate.axes[i] ? 0 : (pad.axes[i] ?? 0));
      const ax = axis(0);
      const ay = axis(1);
      if (ax * ax + ay * ay >= DEADZONE * DEADZONE) {
        move.x = ax;
        move.y = ay;
      } else {
        move.x = (down(pad, [DPAD.right], gate) ? 1 : 0) - (down(pad, [DPAD.left], gate) ? 1 : 0);
        move.y = (down(pad, [DPAD.down], gate) ? 1 : 0) - (down(pad, [DPAD.up], gate) ? 1 : 0);
      }
      // El juego espera una direccion de longitud 0 o 1: el stick marca hacia
      // donde, no cuanto (igual que WASD).
      normalize(move);
      set("kick", down(pad, KICK_BUTTONS, gate));
      set("dash", down(pad, DASH_BUTTONS, gate));
      return frame;
    },
    endTick(): void {
      pressed.clear();
    },
    dispose(): void {
      held.clear();
      pressed.clear();
    },
  };
}
